/**
 * Aria Chat constants – single source of truth for default model (client).
 * When changing the default model, update DEFAULT_ARIA_MODEL_ID here and
 * DEFAULT_ARIA_MODEL_ID in server/config/aria-config.ts so they stay in sync.
 */

/** Default model ID for Aria chat (used when API default is not yet loaded or for fallback). */
export const DEFAULT_ARIA_MODEL_ID = 'claude-opus-5-5';

/** Previous default (2026-09-29); still in the godmode dropdown. */
export const SONNET_46_MODEL_ID = 'claude-sonnet-4-6';

/** Premium tier model ID (e.g. Opus). Keep in sync with server/config/aria-config.ts PREMIUM_ARIA_MODEL_ID. */
export const PREMIUM_ARIA_MODEL_ID = 'claude-opus-5-5';

/** Sonnet 5.5 (Bloom 2.1) as a raw model; godmode dev dropdown only. Keep in sync with server EXPERIMENTAL_GODMODE_ARIA_MODEL_ID. */
export const EXPERIMENTAL_GODMODE_ARIA_MODEL_ID = 'claude-sonnet-5-5';

/** Model the chat retries on when a request fails (godmode; others are coerced server-side). Keep in sync with server OPENAI_FALLBACK_MODEL_ID. */
export const OPENAI_FALLBACK_MODEL_ID = 'gpt-6.1-sol';

/** Exact text sent by the “Show Me What You See” button — must match server SHOW_ME_ARIA_USER_PROMPT. */
export const SHOW_ME_ARIA_USER_PROMPT = 'Show me what you see about me so far.';

/** Default Ask Aria standalone tier for new signups and when the user record has no tier; keep in sync with server. */
export const DEFAULT_ARIA_STANDALONE_TIER = 'trial' as const;

/** Aria Coach consumer entity — keep in sync with server/constants.ts ARIA_COACH_ENTITY_ID. */
export const ARIA_COACH_ENTITY_ID = '6876ca0cde0db79fae68187c';

/**
 * Legacy model IDs that should be cleared from localStorage and replaced with DEFAULT_ARIA_MODEL_ID.
 * Add superseded model IDs here whenever the default is bumped.
 */
export const LEGACY_MODEL_IDS = [
  'claude-sonnet-4-5',
  'claude-sonnet-5',
  SONNET_46_MODEL_ID,
  EXPERIMENTAL_GODMODE_ARIA_MODEL_ID,
  // Retired / removed OpenAI models (2026-10)
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-thinking-mini',
  'gpt-4o-mini',
  'gpt-6-astra',
  'gpt-6-luna',
];

/**
 * Models only selectable in the godmode dev dropdown.
 * Keep in sync with server/config/aria-config.ts GODMODE_ONLY_ARIA_MODEL_IDS.
 */
export const GODMODE_ONLY_ARIA_MODEL_IDS: readonly string[] = [
  EXPERIMENTAL_GODMODE_ARIA_MODEL_ID,
];

/** Human-readable labels for the godmode model dropdown. */
export const ARIA_MODEL_DISPLAY_NAMES: Record<string, string> = {
  [OPENAI_FALLBACK_MODEL_ID]: 'GPT-6.1 Sol',
  'gpt-6.1-terra': 'GPT-6.1 Terra',
  [SONNET_46_MODEL_ID]: 'Claude Sonnet 4.6',
  [EXPERIMENTAL_GODMODE_ARIA_MODEL_ID]: 'Claude Sonnet 5.5',
  [PREMIUM_ARIA_MODEL_ID]: 'Claude Opus 5.5',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
  'grok-4-1-fast-reasoning': 'Grok 4.1 Fast (Reasoning)',
  'grok-4-1-fast-non-reasoning': 'Grok 4.1 Fast',
  'grok-3-mini': 'Grok 3 Mini',
};

/** Show “trial ending” overlay when this many full days or fewer remain (must be &gt; 0). */
export const ARIA_TRIAL_EXPIRING_WARN_DAYS_THRESHOLD = 2;

/** Delay before showing expiring notice after session clock starts (ms). */
export const ARIA_TRIAL_EXPIRING_POST_LOGIN_DELAY_MS = 5 * 60 * 1000;

export const LS_ARIA_TRIAL_EXPIRING_DISMISSED_DATE = 'aria_trial_expiring_dismissed_date';
export const SS_ARIA_TRIAL_EXPIRING_SHOWN_SESSION = 'aria_trial_expiring_shown_session';
export const SS_ARIA_SESSION_CLOCK_START = 'aria_session_clock_start';

/** Dispatched to open Address book / connections manager from standalone trial UI. */
export const ARIA_OPEN_CONNECTIONS_MANAGER_EVENT = 'aria-open-connections-manager';
